import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import RequireAuth from './components/RequireAuth';
import { useAuth } from './contexts/AuthContext';
import PreviewContext from './contexts/PreviewContext';
import { db } from './lib/firebase';
import AppPreviewPanel from './components/AppPreviewPanel';

/** Only renders children when Firestore config/portal.debug_enabled is true. */
function DebugGate({ children }) {
  const [allowed, setAllowed] = useState(null);
  useEffect(() => {
    getDoc(doc(db, 'config', 'portal'))
      .then((snap) => setAllowed(snap.exists() && snap.data().debug_enabled === true))
      .catch(() => setAllowed(false));
  }, []);
  if (allowed === null) return <div className="page"><p>Loading...</p></div>;
  if (!allowed) return <Navigate to="/" replace />;
  return children;
}

function RootRedirect() {
  const { user, portalRole, loading, isGlobalAdmin } = useAuth();
  if (loading) return <div className="page"><p>Loading...</p></div>;
  if (!user || !portalRole) return <Navigate to="/login" replace />;
  if (isGlobalAdmin) return <Navigate to="/admin" replace />;
  return <Navigate to={`/clubs/${portalRole.clubId}`} replace />;
}
import ClubDashboard from './pages/ClubDashboard';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import CourseCards from './pages/CourseCards';
import CourseViewer from './pages/CourseViewer';
import FleetClassManager from './pages/FleetClassManager';
import VersionHistory from './pages/VersionHistory';
import PublishedBundleView from './pages/PublishedBundleView';
import ClubManager from './pages/ClubManager';
import FeatureMatrix from './pages/FeatureMatrix';
import AdminHome from './pages/AdminHome';
import GatingModel from './pages/GatingModel';
import ConfigLevers from './pages/ConfigLevers';
import InterestRegistrations from './pages/InterestRegistrations';
import Products from './pages/Products';
import DocumentManager from './pages/DocumentManager';
import NotFound from './pages/NotFound';
import AuthDebug from './pages/AuthDebug';
import BetaRequests from './pages/BetaRequests';
import ContentManager from './pages/ContentManager';
import Racetimes from './pages/Racetimes';
import SeasonManager from './pages/SeasonManager';
import ReleaseNotes from './pages/ReleaseNotes';
import ReleaseChecklist from './pages/ReleaseChecklist';
import RaceLive from './pages/RaceLive';
import RaceDayLayout from './pages/RaceDayLayout';
import MarksManager from './pages/MarksManager';
import RaceReports from './pages/RaceReports';
import JoinHandler from './pages/JoinHandler';
import AdminDiagnostics from './pages/AdminDiagnostics';
import LiveActivity from './pages/LiveActivity';
import EventIndexManager from './pages/EventIndexManager';
import OnboardUplink from './pages/OnboardUplink';

export default function App() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewCtx = { isOpen: previewOpen, toggle: () => setPreviewOpen(v => !v) };

  return (
    <PreviewContext.Provider value={previewCtx}>
    <div className={previewOpen ? 'app-with-preview' : ''}>
    <Routes>
      {/* Live race viewer — no auth gate, session token in URL is the credential */}
      <Route path="/race-live" element={<RaceLive />} />
      {/* Subdomain-swap entry point — committee.racemarks.app/join?r=<path>.
          Decides where to send the user (login / unauthorized / diagnostics). */}
      <Route path="/join" element={<JoinHandler />} />
      <Route
        path="/admin/diagnostics"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <AdminDiagnostics />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <LiveActivity />
          </RequireAuth>
        }
      />
      {/* Debug page — gated by Firestore config/portal.debug_enabled */}
      <Route path="/debug/auth" element={<DebugGate><AuthDebug /></DebugGate>} />
      <Route path="/login" element={<Login />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route
        path="/clubs/:clubId"
        element={
          <RequireAuth>
            <ClubDashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/course-cards"
        element={
          <RequireAuth>
            <CourseCards />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/courses/viewer"
        element={
          <RequireAuth>
            <CourseViewer />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/courses/history"
        element={
          <RequireAuth>
            <VersionHistory />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/courses/published/:bundleId"
        element={<PublishedBundleView />}
      />
      <Route
        path="/clubs/:clubId/events/:eventId/published/:bundleId"
        element={<PublishedBundleView />}
      />
      <Route
        path="/clubs/:clubId/fleets"
        element={
          <RequireAuth>
            <FleetClassManager />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/documents"
        element={
          <RequireAuth>
            <DocumentManager />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/race-day"
        element={
          <RequireAuth>
            <RaceDayLayout />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/race-day/:raceDayId"
        element={
          <RequireAuth>
            <RaceDayLayout />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/marks"
        element={
          <RequireAuth>
            <MarksManager />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/reports"
        element={
          <RequireAuth>
            <RaceReports />
          </RequireAuth>
        }
      />
      {/* Onboard uplink records (ra-gk3h). firestore.rules gates
          nmea_uplink_manifest at club-admin — a RaceOfficer gets neither `get`
          nor `list` — so the route denies them here rather than letting them
          reach the page and collect a permission error. GlobalAdmin still
          passes (checkPortalAccess short-circuits on it). The dashboard card is
          adminOnly to match. */}
      <Route
        path="/clubs/:clubId/uplink"
        element={
          <RequireAuth allowedRoles={['ClubAdmin']}>
            <OnboardUplink />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/seasons"
        element={
          <RequireAuth>
            <SeasonManager />
          </RequireAuth>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <AdminHome />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/clubs"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <ClubManager />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/interest"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <InterestRegistrations />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/features"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <FeatureMatrix />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/products"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <Products />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/gating-model"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <GatingModel />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/config"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <ConfigLevers />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/beta"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <BetaRequests />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/content"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <ContentManager />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/racetimes"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <Racetimes />
          </RequireAuth>
        }
      />
      <Route
        path="/clubs/:clubId/racetimes"
        element={
          <RequireAuth>
            <Racetimes />
          </RequireAuth>
        }
      />
      <Route
        path="/changelog"
        element={
          <RequireAuth>
            <ReleaseNotes />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/event-index"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <EventIndexManager />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/release-checklist"
        element={
          <RequireAuth allowedRoles={['GlobalAdmin']}>
            <ReleaseChecklist />
          </RequireAuth>
        }
      />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    <AppPreviewPanel isOpen={previewOpen} onClose={() => setPreviewOpen(false)} />
    </div>
    </PreviewContext.Provider>
  );
}
